// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Alert, Button, Select } from '@imprivata-cloud/design-system';
import { ImprSwitch } from '@imprivata-cloud/components';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Entitlement } from '@api/constants';
import { useLandingPage } from '@portal/hooks/useLandingPage';
import { ITDR_LINK_KEY, RISK_AWARENESS_KEYS } from './labels';
import { useRuleSets } from './useRules';
import { ExternalLink } from 'lucide-react';

interface RiskAwarenessSectionProps {
  isOn: boolean;
  ruleSetId: string | null;
  disabled: boolean;
  onToggle: (on: boolean) => void;
  onRuleSetChange: (id: string | null) => void;
}

export const RiskAwarenessSection = ({
  isOn,
  ruleSetId,
  disabled,
  onToggle,
  onRuleSetChange,
}: RiskAwarenessSectionProps) => {
  const { t } = useTranslation();
  // The design-system Select only ties its <label> to the trigger when given an id.
  const ruleSetPickerId = useId();
  const { data: ruleSets, isError } = useRuleSets(true);
  const { data: landingPage } = useLandingPage();

  const itdrUrl = landingPage?.productInfo.purchasedProducts
    .find(p => p.productTag === Entitlement.RISK_BASED_ACCESS)
    ?.startSsoLinks[0]?.startSsoUrl;

  const items =
    ruleSets?.map(rs => ({
      label: rs.name,
      value: rs.id,
    })) ?? [];

  const switchLabel = t(RISK_AWARENESS_KEYS.switchLabel);

  return (
    <div className="conditions-risk-awareness">
      <ImprSwitch
        aria-label={switchLabel}
        checked={isOn}
        disabled={disabled}
        onChange={checked => onToggle(checked)}
        switchLabel={{ labelOn: switchLabel, labelOff: switchLabel }}
      />
      <p className="conditions-risk-awareness-helper">
        {t(RISK_AWARENESS_KEYS.helper)}
      </p>

      {isOn && (
        <>
          {isError ? (
            <Alert
              status="error"
              title={t(RISK_AWARENESS_KEYS.ruleSetsLoadFailedTitle)}
              message={t(RISK_AWARENESS_KEYS.ruleSetsLoadFailedMessage)}
            />
          ) : (
            <Select
              id={ruleSetPickerId}
              label={t(RISK_AWARENESS_KEYS.ruleSetLabel)}
              items={items}
              value={ruleSetId}
              disabled={disabled}
              onSelectionChange={value => onRuleSetChange(value)}
            />
          )}
          {!ruleSetId && (
            <p className="conditions-risk-awareness-no-ruleset">
              {t(RISK_AWARENESS_KEYS.noRuleSet)}
            </p>
          )}
          {itdrUrl !== undefined && (
            <div className="conditions-itdr-link-container">
              <Button
                variant="link"
                iconLeft={<ExternalLink aria-hidden="true" />}
                className="conditions-itdr-link"
                href={itdrUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(ITDR_LINK_KEY)}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
